import {MAX_STALE, SAVE_INTERVAL} from 'notebook/store/auto-save/AutoSaverConstants';
import delegate from 'notebook/workers/core/delegate';
import enabled from 'notebook/workers/core/enabled';
import observe from 'notebook/workers/core/observe';
import timeBound from 'notebook/workers/core/timeBound';
import work from 'notebook/workers/core/work';
import * as logging from 'util/Logging';

export const TIMEOUT_INTERVAL = SAVE_INTERVAL * MAX_STALE;
export const TIMEOUT_STATE = {timedOut: false};

/**
 * Create a workerized variant of the function. This will return a new function which:
 * - Runs the original function in a web worker (if workers are enabled)
 * - Handles fallbacks if workers are disabled, or if errors/timeouts occur
 * - Logs performance data for the worker
 * - Logs warnings if the worker fails unexpectedly
 * @param name the name of the worker (for performance logging purposes)
 * @param fn the function to run on the worker
 * @returns a new function which runs the original function in a web worker
 */
export default function workerize<T, U>(name: string, fn: (object: T) => U): (object: T) => Promise<U> {
  const backgroundFn = delegate(work(fn));
  return async (object: T): Promise<U> => {
    const outcomes = observe(name, backgroundFn, fn, object);
    if (!enabled(TIMEOUT_STATE.timedOut)) {
      return outcomes.disabled();
    }
    const timeoutOnce = (): Promise<U> => {
      TIMEOUT_STATE.timedOut = true;
      logging.error('Content worker timed out unexpectedly.');
      return outcomes.timeout();
    };
    try {
      return await timeBound(outcomes.dispatched(), outcomes.success, timeoutOnce, TIMEOUT_INTERVAL);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (err) {
      logging.error('Content worker failed unexpectedly', {extra: {err}});
      return outcomes.error();
    }
  };
}
